import { FullUser, FullUserResponse } from '../UserApi/UserModel';

export enum ContentType {
	FILE = 'FILE',
	LINK = 'LINK',
	DISCUSSION = 'DISCUSSION',
}

interface AbstractContents<T> {
	contents: T[];
	// comma delimited list of already retrieved discussion IDs. Returned with a previously paged list of Contents
	bufferedMessageIds: string;
	// comma delimited list of already retrieved file IDs. Returned with a previously paged list of
	// Contents. Not required if discussions are not in the retrieved objects.
	bufferedFileIds: string;
}

export type ContentsResponseV2 = AbstractContents<ContentResponseV2>;
export type ContentsV2 = AbstractContents<ContentV2>;

interface AbstractContentV2<T = FullUser> {
	id: string;
	groupId: string;
	name: string;
	description: string;
	createdDateTime: string;
	categories: string[];
	// user who created/uploaded the content
	creator: T;
	reactions: ContentReaction[];
}

type AbstractContentV2Response = AbstractContentV2<FullUserResponse>;

export type ContentV2 = LinkContentItem
	| FileContentItem
	| DiscussionContentItem;

export type ContentResponseV2 = LinkContentResponse
	| FileContentResponse
	| DiscussionContentResponse;

export interface LinkContentItem extends AbstractContentV2 {
	type: ContentType.LINK;
	metadata: LinkContentMetadata;
}
export interface LinkContentMetadata {
	url: string;
	attachedToDiscussions: Discussion[] | null;
}
export interface LinkContentResponse extends AbstractContentV2Response {
	type: ContentType.LINK;
	metadata: LinkContentMetadataResponse;
}
export interface LinkContentMetadataResponse {
	url: string;
	attachedToDiscussions: DiscussionResponse[] | null;
}

export interface FileContentItem extends AbstractContentV2 {
	type: ContentType.FILE;
	metadata: FileContentMetadata;
}
export interface FileContentMetadata {
	byteSize: number;
	attachedToDiscussions: Discussion[] | null;
}

export interface FileContentResponse extends AbstractContentV2Response {
	type: ContentType.FILE;
	metadata: FileContentMetadataResponse;
}
export interface FileContentMetadataResponse {
	byteSize: number;
	attachedToDiscussions: DiscussionResponse[] | null;
}

export interface DiscussionContentItem extends AbstractContentV2 {
	type: ContentType.DISCUSSION;
	metadata: DiscussionContentMetadata;
}
export interface DiscussionContentMetadata {
	discussion: Discussion;
}

export interface StoredDiscussionContentItem extends AbstractContentV2 {
	type: ContentType.DISCUSSION;
	metadata: StoredDiscussionContentMetadata;
}

export interface StoredDiscussionContentMetadata {
	discussion: StoredDiscussion;
}

export interface StoredDiscussion extends Omit<Discussion, 'messages'> {
	messages: string[];
}

export interface DiscussionContentResponse extends AbstractContentV2Response {
	type: ContentType.DISCUSSION;
	metadata: DiscussionContentMetadataResponse;
}
export interface DiscussionContentMetadataResponse {
	discussion: DiscussionResponse;
}

export type PartialContents = Partial<Content>[];

/**
 * @deprecated
 */
export interface CreateDiscussionPayload {
	groupId: string;
	messageText: string;
	subject: string;
	following: boolean;
	notifyMembers: boolean;
	categories: Array<string>;
	attachments: PartialContents;
}

export interface CreateDiscussionPayloadV2 {
	// groupId: string;
	messageText: string;
	subject: string;
	following: boolean;
	notifyMembers: boolean;
	categories: Array<string>;
	attachments: PartialContents;
}

export interface EditDiscussionPayload {
	groupId: string;
	subject: string;
}

/**
 * @deprecated
 */
export interface ReplyDiscussionPayload extends CreateDiscussionPayload {
	version: number;
	parentId: string;
}

export interface ReplyDiscussionPayloadV2 extends CreateDiscussionPayloadV2 {
	version: number;
}

export interface DiscussionResponse {
	messages: DiscussionMessageResponse[];
	followers: FullUserResponse[];
	categories: string[];
	read: boolean;
}

export interface Discussion {
	messages: DiscussionMessage[];
	followers: FullUser[];
	categories: string[];
	read: boolean;
}

/**
 * @deprecated
 */
export interface Content {
	id: string;
	groupId: string;
	type: ContentType;
	name: string;
	description: string;
	// The URL to the content
	source: string;
	// size of file in bytes. blank for web links
	byteSize: number | null;
	created: number;
	version: number;
	categories: string[];
	// user who created/uploaded the content
	creator: FullUser;
	discussion: Discussion | null;
	attachedToDiscussions: Discussion[] | null;
}

/**
 * @deprecated
 */
export type ContentsResponse = AbstractContents<ContentResponse>;

/**
 * @deprecated
 */
export interface Contents {
	contents: Content[];
	// comma delimited list of already retrieved discussion IDs. Returned with a previously paged list of Contents
	bufferedMessageIds: string;
	// comma delimited list of already retrieved file IDs. Returned with a previously paged list of
	// Contents. Not required if discussions are not in the retrieved objects.
	bufferedFileIds: string;
}

/**
 * @deprecated
 */
export interface ContentResponse {
	id: string;
	groupId: string;
	type: ContentType;
	name: string;
	description: string;
	// The URL to the content
	source: string;
	// size of file in bytes. blank for web links
	byteSize: number | null;
	created: number;
	version: number;
	categories: string[];
	// user who created/uploaded the content
	creator: FullUserResponse;
	discussion: DiscussionResponse | null;
	attachedToDiscussions: DiscussionResponse[] | null;
}

export interface DiscussionMessage {
	id: string;
	groupId: string;
	// Hashed ID of previous message in thread
	parentId: string | null;
	subject: string;
	// Base64 encoded
	messageText: string;
	// Indicates if current user is following the message
	following: boolean;
	// indicates if group members should be notified on message create
	notifyMembers: boolean;
	// indicates if the message was read
	// TODO: BY WHOM?
	read: boolean;
	created: number | string;
	version: number;
	sender: FullUser;
	// indicates time the message text is hidden or removed
	removedAt: Date | null;
	removedBySender: boolean;
	attachments: ContentsV2; // Contents;
	// categories for the message
	categories: Array<string>;
	// The sidebar/note info
	numNotes: number;
	numUnreadNotes: number;
	reactions: ContentReaction[];
}

export interface DiscussionMessageResponse {
	id: string;
	groupId: string;
	// Hashed ID of previous message in thread
	parentId: string | null;
	subject: string;
	// Base64 encoded
	messageText: string;
	// Indicates if current user is following the message
	following: boolean;
	// indicates if group members should be notified on message create
	notifyMembers: boolean;
	// indicates if the message was read
	// TODO: BY WHOM?
	read: boolean;
	created: number;
	version: number;
	sender: FullUserResponse;
	// indicates time the message text is hidden or removed
	removedAt: Date | null;
	removedBySender: boolean;
	attachments: ContentsV2; // Contents;
	// categories for the message
	categories: Array<string>;
	// The sidebar/note info
	numNotes: number;
	numUnreadNotes: number;
	reactions: ContentReaction[];
}

export interface ContentReaction {
	contentId: string;
	userId: string;
	reactionType: ReactionType;
}

export enum ReactionType {
	LIKE = "LIKE",
	CLAP = "CLAP",
	HAPPY = "HAPPY",
	LAUGH = "LAUGH",
	FOLDED_HANDS = "FOLDED_HANDS",
	SAD = "SAD",
}
